import { UcoastEl } from '@/scripts/core/UcoastEl'
import { motionEnabled } from '@/scripts/core/motion'
import { FEATURED_PRODUCT_VARIANT_CHANGE_EVENT } from '@/scripts/theme/constants'

export class GalleryCarousel extends UcoastEl {
	static htmlSelector = 'gallery-carousel'

	private cards: NodeListOf<HTMLElement>
	private total = 0
	private current = 0
	private dragStart = 0
	private dragging = false

	private counter: HTMLElement | null
	private prevButton: HTMLButtonElement | null
	private nextButton: HTMLButtonElement | null
	private featuredProduct: HTMLElement | null

	constructor() {
		super()

		this.cards = this.querySelectorAll<HTMLElement>('.gallery-card')
		this.total = this.cards.length

		this.counter = this.querySelector<HTMLElement>('.gallery-counter')
		this.prevButton =
			this.querySelector<HTMLButtonElement>('.gallery-arrow-left')
		this.nextButton =
			this.querySelector<HTMLButtonElement>('.gallery-arrow-right')
		this.featuredProduct = this.closest<HTMLElement>(
			'.featured-product-carousel'
		)

		this.init()
	}

	private init(): void {
		if (!this.total) return

		this.bindEvents()
		this.arrange()
	}

	private arrange(): void {
		this.cards.forEach((card, index) => {
			const offset = index - this.current

			if (offset < 0) {
				card.style.transform =
					'translate3d(-105%, 0, 0) rotate(-1deg)'
				card.style.opacity = '0'
				card.style.zIndex = `${index}`
			} else if (offset === 0) {
				card.style.transform =
					'translate3d(0, 0, 0) rotate(0deg)'
				card.style.opacity = '1'
				card.style.zIndex = `${this.total}`
			} else if (offset <= 2) {
				card.style.transform = `
					translate3d(${offset * 2.5}%, ${offset * -4}px, 0)
					rotate(${offset * 0.5}deg)
				`
				card.style.opacity = `${1 - offset * 0.12}`
				card.style.zIndex = `${this.total - offset}`
			} else {
				card.style.transform =
					'translate3d(7.5%, -12px, 0) rotate(1.5deg)'
				card.style.opacity = '0'
				card.style.zIndex = '0'
			}
		})

		this.updateControls()
	}

	private updateControls(): void {
		if (this.counter) {
			this.counter.textContent =
				`${this.current + 1} / ${this.total}`
		}

		if (this.prevButton) {
			this.prevButton.disabled = this.current === 0
		}

		if (this.nextButton) {
			this.nextButton.disabled =
				this.current === this.total - 1
		}
	}

	private go(index: number, animate = true): void {
		if (index < 0 || index >= this.total) return

		this.setCardTransition(animate)

		this.current = index
		this.arrange()
	}

	private bindEvents(): void {
		this.featuredProduct?.addEventListener(
			FEATURED_PRODUCT_VARIANT_CHANGE_EVENT,
			this.handleVariantChange
		)

		this.prevButton?.addEventListener('click', () => {
			this.go(this.current - 1)
		})

		this.nextButton?.addEventListener('click', () => {
			this.go(this.current + 1)
		})

		this.addEventListener('mousedown', (event) => {
			const target = event.target as HTMLElement

			if (target.closest('.gallery-carousel-buttons')) return

			this.dragging = true
			this.dragStart = event.clientX

			this.cards.forEach((card) => {
				card.style.transition = 'none'
			})
		})

		this.addEventListener(
			'touchstart',
			(event) => {
				this.dragging = true
				this.dragStart = event.touches[0].clientX

				this.cards.forEach((card) => {
					card.style.transition = 'none'
				})
			},
			{ passive: true }
		)

		window.addEventListener('mousemove', this.handleMouseMove)
		window.addEventListener('mouseup', this.handleMouseUp)

		window.addEventListener(
			'touchmove',
			this.handleTouchMove,
			{ passive: true }
		)

		window.addEventListener('touchend', this.handleTouchEnd)

		this.addEventListener('keydown', this.handleKeydown)
	}

	private handleMouseMove = (event: MouseEvent): void => {
		if (!this.dragging) return

		this.updateDrag(event.clientX)
	}

	private handleTouchMove = (event: TouchEvent): void => {
		if (!this.dragging) return

		this.updateDrag(event.touches[0].clientX)
	}

	private updateDrag(clientX: number): void {
		const diff = clientX - this.dragStart
		const activeCard = this.cards[this.current]

		if (!activeCard) return

		activeCard.style.transform = `
			translate3d(${diff}px, 0, 0)
			rotate(${diff * 0.03}deg)
		`
	}

	private handleMouseUp = (event: MouseEvent): void => {
		this.dragEnd(event.clientX)
	}

	private handleTouchEnd = (event: TouchEvent): void => {
		this.dragEnd(event.changedTouches[0].clientX)
	}

	private dragEnd(endX: number): void {
		if (!this.dragging) return

		this.dragging = false

		const diff = endX - this.dragStart

		this.setCardTransition()

		if (diff < -60 && this.current < this.total - 1) {
			this.current++
		} else if (diff > 60 && this.current > 0) {
			this.current--
		}

		this.arrange()
	}

	private setCardTransition(animate = true): void {
		const transition = !animate
			? 'none'
			: motionEnabled()
			? 'transform 0.75s cubic-bezier(.645, .045, .355, 1), opacity 0.75s cubic-bezier(.645, .045, .355, 1)'
			: 'opacity 0.2s ease'

		this.cards.forEach((card) => {
			card.style.transition = transition
		})
	}

	private handleKeydown = (event: KeyboardEvent): void => {
		if (event.target !== this) return
		if (event.key === 'ArrowLeft') {
			event.preventDefault()
			this.go(this.current - 1, false)
		}

		if (event.key === 'ArrowRight') {
			event.preventDefault()
			this.go(this.current + 1, false)
		}
	}

	private handleVariantChange = (event: Event): void => {
		const variantId = (event as CustomEvent<{ variantId?: string | number }>).detail
			?.variantId

		if (variantId === undefined || variantId === null || variantId === '') return
		const normalizedVariantId = String(variantId)

		const index = Array.from(this.cards).findIndex((card) =>
			(card.dataset.variantIds ?? '')
				.split(',')
				.map((id) => id.trim())
				.includes(normalizedVariantId)
		)

		if (index >= 0) this.go(index)
	}

	override disconnectedCallback(): void {
		this.featuredProduct?.removeEventListener(
			FEATURED_PRODUCT_VARIANT_CHANGE_EVENT,
			this.handleVariantChange
		)
		window.removeEventListener('mousemove', this.handleMouseMove)
		window.removeEventListener('mouseup', this.handleMouseUp)
		window.removeEventListener('touchmove', this.handleTouchMove)
		window.removeEventListener('touchend', this.handleTouchEnd)
		this.removeEventListener('keydown', this.handleKeydown)
		super.disconnectedCallback()
	}
}
